import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, SectionList, StyleSheet, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';
import moment from 'moment';

import i18n from '../../i18n';
import { track } from '../../utils/analytics';
import { logErrorWithContext } from '../../utils/sentry/sentrySetup';

import Text from '../global/Text';
import ConfirmModal from '../global/ConfirmModal';
import fontMaker from '../../utils/font';
import { NIMBLY_API_SCHEDULES_LITE_INDICATOR } from '../../constants/api';
import { colors } from '../../constants/theme';
import API from '../../store/helpers/api';
import { RootState } from '../../store/reducers';
import { scheduleSitesActions, scheduleSitesThunk } from '../../store/reducers/schedulesites';
import { activeScheduleThunk } from '../../store/reducers/activeschedule';
import { OrganizationSchedule } from '../../utils/classes';
import { currentPeriodEndDate, currentPeriodStartDate } from '../../utils/schedule';
import RootNavigation from '../../RootNavigation';
import type { ScheduleListItemData, ScheduleToggleMode, ViewByMode } from '../../typing/scheduleItem';
import type { ScheduleFilterState } from '../../typing/scheduleFilter';
import { DEFAULT_FILTER_STATE, isFilterActive as checkFilterActive } from '../../typing/scheduleFilter';
import { getScheduleListItemIdentity } from '../../utils/scheduleMapper';
import { IS_SHORT_VIEWPORT } from '../../utils/screensize';

import ScheduleCalendar from './ScheduleCalendar';
import ScheduleToggle from './ScheduleToggle';
import ScheduleSearchBar from './ScheduleSearchBar';
import ViewByChips from './ViewByChips';
import ScheduleListItem from './ScheduleListItem';
import HomeCheckInModal from './HomeCheckInModal';
import ScheduleFilterModal from './ScheduleFilterModal';
import { getScheduleCalendarIndicatorRange, mapScheduleIndicatorToMarkedDates } from './utils/scheduleCalendar';
import useHomeCheckIn from './useHomeCheckIn';
import { reconcileScheduleItemsWithLocalState } from './utils/reconcileScheduleItemsWithLocalState';
import { groupActiveScheduleByDate, type ActiveDateSection } from './utils/groupActiveScheduleByDate';

import { selectProfile } from '../../store/selectors/profile';
const SCHEDULE_REFRESH_INTERVAL_MS = 60 * 1000;
const REQUEST_DEBOUNCE_MS = 1500;

type GroupedScheduleSection = {
  key: string;
  title: string;
  totalCount: number;
  data: ScheduleListItemData[];
};

const NewHomeScreen: React.FC = () => {
  const { t } = useTranslation('home');
  const dispatch = useDispatch();
  const requestTsRef = useRef<{ date: string; timestamp: number }>({ date: '', timestamp: 0 });
  const indicatorRequestRef = useRef<{ key: string; timestamp: number }>({ key: '', timestamp: 0 });
  const latestIndicatorKeyRef = useRef('');
  const indicatorCacheRef = useRef<Record<string, Record<string, { activeCount: number; totalCount: number }>>>({});

  // Redux state
  const scheduleItems = useSelector<RootState, ScheduleListItemData[]>(state => state.scheduleSites.myScheduleItems);
  const cacheReports = useSelector<RootState, RootState['reportcache']['reports']>(state => state.reportcache.reports);
  const cacheSummaries = useSelector<RootState, RootState['reportcache']['summaries']>(
    state => state.reportcache.summaries
  );
  const pendingSites = useSelector<RootState, RootState['reportsubmit']['pendingSites']>(
    state => state.reportsubmit.pendingSites
  );
  const totalPercentage = useSelector<RootState, number>(state => state.scheduleSites.totalPercentage);
  const isFetching = useSelector<RootState, boolean>(state => state.scheduleSites.isFetchingMySites);
  const hasFetchError = useSelector<RootState, boolean>(state => state.scheduleSites.hasFetchError);
  const timestamp = useSelector<RootState, number | null>(state => state.scheduleSites.timestamp);
  // Active Schedules tab — full active-period data (separate slice)
  const activeItems = useSelector<RootState, ScheduleListItemData[]>(state => state.activeSchedule.items);
  const activeTotalPercentage = useSelector<RootState, number>(state => state.activeSchedule.totalPercentage);
  const activeIsFetching = useSelector<RootState, boolean>(state => state.activeSchedule.isFetching);
  const activeHasError = useSelector<RootState, boolean>(state => state.activeSchedule.hasError);
  const activeTimestamp = useSelector<RootState, number | null>(state => state.activeSchedule.timestamp);
  const lastFetchedStartDate = useSelector<RootState, string | null>(state => state.scheduleSites.lastFetchedStartDate);
  const profileColor = useSelector<RootState, string>(state => state.profiletheme.color);
  const isOffline = useSelector<RootState, boolean>(state => !state.network.isConnected);
  const language = useSelector<RootState, string>(state => selectProfile(state)?.language || 'en');
  const organizationSchedule = useSelector<RootState, OrganizationSchedule | null | undefined>(
    state => state.organization.myOrganization?.schedule
  );

  // Check-in state
  const [selectedItem, setSelectedItem] = useState<ScheduleListItemData | null>(null);
  const [showCheckInModal, setShowCheckInModal] = useState(false);

  const {
    isCheckingIn,
    showConfirmModal,
    modalConfig,
    setShowConfirmModal,
    handleCheckIn,
    handleContinueDraft,
    handleResetDraft,
    handleSyncWithServer,
    handleModifyPendingUpload
  } = useHomeCheckIn();

  const resolvedScheduleItems = useMemo(
    () => reconcileScheduleItemsWithLocalState(scheduleItems, cacheReports, cacheSummaries, pendingSites),
    [scheduleItems, cacheReports, cacheSummaries, pendingSites]
  );

  const resolvedActiveItems = useMemo(
    () => reconcileScheduleItemsWithLocalState(activeItems, cacheReports, cacheSummaries, pendingSites),
    [activeItems, cacheReports, cacheSummaries, pendingSites]
  );

  const [today, setToday] = useState(() => moment().locale('en').format('YYYY-MM-DD'));
  // Filter & Sort state
  const [filterState, setFilterState] = useState<ScheduleFilterState>(DEFAULT_FILTER_STATE);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  // Accumulate all sites ever seen so the filter modal shows all options
  // even when a site filter is active and the API only returns filtered items.
  const [allKnownSites, setAllKnownSites] = useState<{ id: string; name: string }[]>([]);
  // Local filter state
  const [toggle, setToggle] = useState<ScheduleToggleMode>('today');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewBy, setViewBy] = useState<ViewByMode>('all');
  const [selectedDate, setSelectedDate] = useState(() => moment().locale('en').format('YYYY-MM-DD'));
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => moment().startOf('month').valueOf());
  const [calendarMarkedDates, setCalendarMarkedDates] = useState<
    Record<string, { activeCount: number; totalCount: number }>
  >({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  // Active Schedules date sections default to CLOSED (undefined = collapsed); only opened keys are true.
  const [openActiveSections, setOpenActiveSections] = useState<Record<string, boolean>>({});

  const fetchSchedules = useCallback(
    (date: string, force = false, filter?: ScheduleFilterState) => {
      if (!organizationSchedule || isFetching) {
        return;
      }

      const normalizedDate = moment(date).format('YYYY-MM-DD');
      const now = Date.now();
      const recentlyRequested =
        requestTsRef.current.date === normalizedDate && now - requestTsRef.current.timestamp < REQUEST_DEBOUNCE_MS;
      if (recentlyRequested && !force) {
        return;
      }

      const hasData = scheduleItems.length > 0 && !!timestamp && lastFetchedStartDate === normalizedDate;
      const isFresh = !!timestamp && now - timestamp < SCHEDULE_REFRESH_INTERVAL_MS;
      if (!force && hasData && isFresh) {
        return;
      }

      const activeFilter = filter ?? filterState;
      const filterParams = {
        sites: activeFilter.sites.length ? activeFilter.sites : undefined,
        status: activeFilter.statuses.length ? activeFilter.statuses : undefined,
        types: activeFilter.types.length ? activeFilter.types : undefined,
        scheduleType: activeFilter.scheduleTypes.length ? activeFilter.scheduleTypes : undefined
      };

      requestTsRef.current = { date: normalizedDate, timestamp: now };
      dispatch(scheduleSitesThunk.getMyScheduledSites(organizationSchedule, normalizedDate, filterParams));
    },
    [dispatch, filterState, isFetching, lastFetchedStartDate, organizationSchedule, scheduleItems.length, timestamp]
  );

  const fetchActiveSchedules = useCallback(
    (force = false, filter?: ScheduleFilterState) => {
      if (!organizationSchedule || activeIsFetching) {
        return;
      }

      const now = Date.now();
      const isFresh = !!activeTimestamp && now - activeTimestamp < SCHEDULE_REFRESH_INTERVAL_MS;
      if (!force && activeItems.length > 0 && isFresh) {
        return;
      }

      const activeFilter = filter ?? filterState;
      const filterParams = {
        sites: activeFilter.sites.length ? activeFilter.sites : undefined,
        status: activeFilter.statuses.length ? activeFilter.statuses : undefined,
        types: activeFilter.types.length ? activeFilter.types : undefined,
        scheduleType: activeFilter.scheduleTypes.length ? activeFilter.scheduleTypes : undefined
      };

      dispatch(activeScheduleThunk.getActivePeriodSchedules(organizationSchedule, filterParams));
    },
    [dispatch, filterState, activeIsFetching, activeTimestamp, activeItems.length, organizationSchedule]
  );

  const fetchCalendarIndicators = useCallback(
    async (monthValue: number, force = false) => {
      const { startDate, endDate } = getScheduleCalendarIndicatorRange(organizationSchedule, monthValue);
      const cacheKey = `${startDate}:${endDate}`;

      if (!force && indicatorCacheRef.current[cacheKey]) {
        setCalendarMarkedDates(indicatorCacheRef.current[cacheKey]);
        return;
      }

      const now = Date.now();
      const recentlyRequested =
        indicatorRequestRef.current.key === cacheKey &&
        now - indicatorRequestRef.current.timestamp < REQUEST_DEBOUNCE_MS;

      if (recentlyRequested) {
        return;
      }

      indicatorRequestRef.current = { key: cacheKey, timestamp: now };
      latestIndicatorKeyRef.current = cacheKey;

      try {
        const api = await API.withToken();
        const qs = API.makeQueryString({ startDate, endDate });
        const res = await api.getJSON(`${NIMBLY_API_SCHEDULES_LITE_INDICATOR}?${qs}`);
        const nextMarkedDates = mapScheduleIndicatorToMarkedDates(res?.data);

        indicatorCacheRef.current[cacheKey] = nextMarkedDates;

        if (latestIndicatorKeyRef.current === cacheKey) {
          setCalendarMarkedDates(nextMarkedDates);
        }
      } catch (error) {
        logErrorWithContext(
          error instanceof Error ? error : new Error(String(error)),
          'schedule_lite_indicator_fetch_error',
          { startDate, endDate }
        );

        if (latestIndicatorKeyRef.current === cacheKey) {
          setCalendarMarkedDates({});
        }
      }
    },
    [organizationSchedule]
  );

  // Force fetch on mount (page reload always gets fresh data).
  // Persisted isFetching/timestamp can block normal fetchSchedules(),
  // so we clear stale isFetching and force-bypass freshness check.
  const mountFetchDone = useRef(false);

  useEffect(() => {
    moment.locale(i18n.language);
    if (!mountFetchDone.current && organizationSchedule) {
      mountFetchDone.current = true;
      if (isFetching) {
        dispatch(scheduleSitesActions.setIsFetchingMySites(false));
      }
      fetchSchedules(selectedDate, true);
    }
  }, [dispatch, fetchSchedules, isFetching, organizationSchedule, selectedDate]);

  useEffect(() => {
    if (!mountFetchDone.current || !organizationSchedule) {
      return;
    }

    fetchSchedules(selectedDate);
  }, [fetchSchedules, organizationSchedule, selectedDate]);

  useEffect(() => {
    fetchCalendarIndicators(calendarMonth);
  }, [calendarMonth, fetchCalendarIndicators]);

  // Fetch the full active-period schedules when the Active Schedules tab is active.
  useEffect(() => {
    if (toggle === 'active') {
      fetchActiveSchedules();
    }
  }, [toggle, fetchActiveSchedules]);

  useFocusEffect(
    useCallback(() => {
      moment.locale(i18n.language);
      const currentDate = moment().locale('en').format('YYYY-MM-DD');
      setToday(prevToday => {
        if (selectedDate === prevToday) {
          setSelectedDate(currentDate);
          setCalendarMonth(moment(currentDate).startOf('month').valueOf());
        }

        return currentDate;
      });
      fetchSchedules(selectedDate);
      if (toggle === 'active') {
        fetchActiveSchedules();
      }
      track('schedule_list_viewed', {
        count: scheduleItems.length,
        toggle,
        viewBy,
        searchQuery
      });
      return () => undefined;
    }, [fetchSchedules, fetchActiveSchedules, scheduleItems.length, selectedDate, searchQuery, toggle, viewBy])
  );

  const calendarRange = useMemo(() => {
    if (!organizationSchedule) {
      return {
        minDate: moment(today).subtract(90, 'days').format('YYYY-MM-DD'),
        maxDate: moment(today).add(30, 'days').format('YYYY-MM-DD')
      };
    }

    const rangeToday = moment(today, 'YYYY-MM-DD');
    const startDateMoment = currentPeriodStartDate(organizationSchedule) || rangeToday.clone();
    const endDateMoment = currentPeriodEndDate(organizationSchedule, startDateMoment.clone()) || rangeToday.clone();

    let minDate = startDateMoment.clone();
    let maxDate = endDateMoment.clone();

    if (maxDate.diff(minDate, 'months') > 1) {
      minDate = rangeToday.clone().subtract(90, 'days');
      maxDate = rangeToday.clone().add(30, 'days');
    }

    return {
      minDate: minDate.format('YYYY-MM-DD'),
      maxDate: maxDate.format('YYYY-MM-DD')
    };
  }, [organizationSchedule, today]);

  const primaryToggleLabel = useMemo(() => {
    if (selectedDate === today) {
      return t('newHome.today');
    }

    return moment(selectedDate).format('D MMM');
  }, [selectedDate, t, today]);

  const calendarToggleLabel = useMemo(
    () => (isCalendarVisible ? t('newHome.hideCalendar') : t('newHome.showCalendar')),
    [isCalendarVisible, t]
  );

  const handleCalendarToggle = useCallback(() => {
    setCalendarMonth(moment(selectedDate).startOf('month').valueOf());
    setIsCalendarVisible(value => !value);
  }, [selectedDate]);

  const handleSelectCalendarDate = useCallback((date: string) => {
    setSelectedDate(date);
    setCalendarMonth(moment(date).startOf('month').valueOf());
    setToggle('today');
  }, []);

  const handleCalendarMonthChange = useCallback(
    (direction: 'prev' | 'next') => {
      const nextMonth = moment(calendarMonth)[direction === 'next' ? 'add' : 'subtract'](1, 'month').startOf('month');

      const minMonth = moment(calendarRange.minDate).startOf('month');
      const maxMonth = moment(calendarRange.maxDate).startOf('month');

      if (nextMonth.isBefore(minMonth, 'month') || nextMonth.isAfter(maxMonth, 'month')) {
        return;
      }

      setCalendarMonth(nextMonth.valueOf());
    },
    [calendarMonth, calendarRange.maxDate, calendarRange.minDate]
  );

  const handleToggleChange = useCallback(
    (next: ScheduleToggleMode) => {
      setToggle(next);
      // Always re-fetch the active period from the API when the tab is clicked.
      if (next === 'active') {
        fetchActiveSchedules(true);
      }
      track('filter_applied', { toggle: next, viewBy, searchQuery });
    },
    [viewBy, searchQuery, fetchActiveSchedules]
  );

  const handleViewByChange = useCallback(
    (next: ViewByMode) => {
      setViewBy(next);
      track('filter_applied', { toggle, viewBy: next, searchQuery });
    },
    [toggle, searchQuery]
  );

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchQuery(text);
      track('filter_applied', { toggle, viewBy, searchQuery: text });
    },
    [toggle, viewBy]
  );

  // When scheduleItems changes, merge any new sites into allKnownSites.
  // We never remove entries so the filter modal always shows every site
  // regardless of which server-side filter is currently active.
  useEffect(() => {
    if (scheduleItems.length === 0) {
      return;
    }
    setAllKnownSites(prev => {
      const knownIds = new Set(prev.map(s => s.id));
      const seenInBatch = new Set<string>();
      const incoming: { id: string; name: string }[] = [];
      for (const item of scheduleItems) {
        if (!knownIds.has(item.siteID) && !seenInBatch.has(item.siteID)) {
          seenInBatch.add(item.siteID);
          incoming.push({ id: item.siteID, name: item.siteName });
        }
      }
      return incoming.length > 0 ? [...prev, ...incoming] : prev;
    });
  }, [scheduleItems]);

  const availableSites = allKnownSites;

  const handleFilterApply = useCallback(
    (newFilter: ScheduleFilterState) => {
      setFilterState(newFilter);
      setIsFilterModalVisible(false);
      if (toggle === 'active') {
        fetchActiveSchedules(true, newFilter);
      } else {
        fetchSchedules(selectedDate, true, newFilter);
      }
      track('filter_applied', { filterState: newFilter });
    },
    [fetchActiveSchedules, fetchSchedules, selectedDate, toggle]
  );

  const hasActiveFilter = checkFilterActive(filterState);

  // Today tab: schedules for the selected date, filtered by search + sorted
  const filteredItems = useMemo(() => {
    let items = resolvedScheduleItems.filter(item => item.scheduledDate === selectedDate);

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      items = items.filter(
        item => item.questionnaireName.toLowerCase().includes(query) || item.siteName.toLowerCase().includes(query)
      );
    }

    // Client-side sort
    const sortFieldMap: Record<string, keyof ScheduleListItemData> = {
      siteName: 'siteName',
      status: 'status',
      type: 'scheduleType'
    };
    const sortKey = sortFieldMap[filterState.sortField];
    if (sortKey) {
      items = [...items].sort((a, b) => {
        const aVal = String(a[sortKey] ?? '');
        const bVal = String(b[sortKey] ?? '');
        return filterState.sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
    }

    return items;
  }, [resolvedScheduleItems, selectedDate, searchQuery, filterState]);

  // Active Schedules tab: full active period, search-filtered (incl. completed), grouped by date
  const activeFilteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return resolvedActiveItems;
    }
    const query = searchQuery.toLowerCase().trim();
    return resolvedActiveItems.filter(
      item => item.questionnaireName.toLowerCase().includes(query) || item.siteName.toLowerCase().includes(query)
    );
  }, [resolvedActiveItems, searchQuery]);

  const activeSections = useMemo<ActiveDateSection[]>(
    () => groupActiveScheduleByDate(activeFilteredItems),
    [activeFilteredItems]
  );

  const activeVisibleSections = useMemo(
    () =>
      activeSections.map(section => ({
        ...section,
        // Default closed: only render rows for sections the user has explicitly opened.
        data: openActiveSections[section.key] ? section.data : []
      })),
    [activeSections, openActiveSections]
  );

  // Group items for section list when viewBy !== 'all'
  const sections = useMemo<GroupedScheduleSection[] | null>(() => {
    if (viewBy === 'all') return null;

    const groupMap = new Map<string, ScheduleListItemData[]>();

    for (const item of filteredItems) {
      let groupKey: string;
      if (viewBy === 'location') {
        groupKey = item.siteName || 'Unknown';
      } else if (viewBy === 'status') {
        groupKey = item.status;
      } else {
        groupKey = item.scheduleType;
      }

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }
      groupMap.get(groupKey)!.push(item);
    }

    return Array.from(groupMap.entries()).map(([groupKey, data]) => ({
      key: `${viewBy}:${groupKey}`,
      title: groupKey.charAt(0).toUpperCase() + groupKey.slice(1).replace('-', ' '),
      totalCount: data.length,
      data
    }));
  }, [filteredItems, viewBy]);

  const visibleSections = useMemo(() => {
    if (!sections) {
      return [];
    }

    return sections.map(section => ({
      ...section,
      data: collapsedSections[section.key] ? [] : section.data
    }));
  }, [collapsedSections, sections]);

  const closeCheckInModal = useCallback(() => {
    setShowCheckInModal(false);
    setSelectedItem(null);
  }, []);

  const handleItemPress = useCallback((item: ScheduleListItemData) => {
    // Show check-in / completed / pending-upload bottom sheet modal.
    // Completed reports surface a "View Report" action inside the modal
    // (matching audit-lite), instead of navigating straight to the Site page.
    setSelectedItem(item);
    setShowCheckInModal(true);
  }, []);

  const handleViewReport = useCallback(() => {
    closeCheckInModal();
    RootNavigation.navigate('ReportsTab', { screen: 'Reports' });
  }, [closeCheckInModal]);

  const lastUpdatedText = useMemo(() => {
    if (!timestamp) return '';
    const m = moment(timestamp).locale('en');
    const now = moment();
    return m.isSame(now, 'day') ? m.format('hh:mm A') : m.format('D MMM YYYY, hh:mm A');
  }, [timestamp]);

  const scheduleCountLabel = useMemo(
    () => (toggle === 'active' ? t('newHome.activeSchedules') : t('newHome.allSchedules')),
    [t, toggle]
  );

  const isActiveMode = toggle === 'active';
  const displayItemCount = isActiveMode ? activeFilteredItems.length : filteredItems.length;
  const displayIsFetching = isActiveMode ? activeIsFetching : isFetching;
  const displayFetchError = isActiveMode ? activeHasError : hasFetchError;
  // Before the active period has been fetched, keep showing the existing progress value.
  const displayPercentage = isActiveMode && activeTimestamp ? activeTotalPercentage : totalPercentage;

  const renderItem = useCallback(
    ({ item }: { item: ScheduleListItemData }) => <ScheduleListItem item={item} onPress={handleItemPress} />,
    [handleItemPress]
  );

  const keyExtractor = useCallback(
    (item: ScheduleListItemData, index: number) => `${getScheduleListItemIdentity(item)}-${index}`,
    []
  );

  const toggleSection = useCallback((sectionKey: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  }, []);

  const toggleActiveSection = useCallback((sectionKey: string) => {
    setOpenActiveSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  }, []);

  const renderSectionHeader = useCallback(
    ({ section }: { section: GroupedScheduleSection }) => {
      const isCollapsed = !!collapsedSections[section.key];

      return (
        <TouchableOpacity activeOpacity={0.8} onPress={() => toggleSection(section.key)} style={styles.sectionHeader}>
          <View style={styles.sectionHeaderTitleWrap}>
            <Text style={styles.sectionHeaderText}>{section.title}</Text>
            <Text style={styles.sectionHeaderCount}>{section.totalCount}</Text>
          </View>
          <Icon
            name={isCollapsed ? 'chevron-down' : 'chevron-up'}
            size={18}
            color={colors.darkgray}
            style={styles.sectionHeaderIcon}
          />
        </TouchableOpacity>
      );
    },
    [collapsedSections, toggleSection]
  );

  const renderActiveSectionHeader = useCallback(
    ({ section }: { section: ActiveDateSection }) => {
      // Default closed: a section is collapsed unless the user has explicitly opened it.
      const isCollapsed = !openActiveSections[section.key];

      return (
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={() => toggleActiveSection(section.key)}
          style={styles.dateSectionHeader}
        >
          <View style={styles.dateSectionHeaderLeft}>
            <Text style={styles.dateSectionHeaderTitle}>{section.title}</Text>
            <View style={styles.reportsCompletedRow}>
              <Text style={styles.reportsCompletedLabel}>{t('newHome.reportsCompleted')}: </Text>
              <Text style={styles.reportsCompletedValue}>
                {section.totalCompleted}/{section.totalCount}
              </Text>
            </View>
          </View>
          <Icon name={isCollapsed ? 'chevron-up' : 'chevron-down'} size={22} color={colors.primary} />
        </TouchableOpacity>
      );
    },
    [openActiveSections, toggleActiveSection, t]
  );

  const renderEmpty = useCallback(
    () => (
      <View style={styles.emptyContainer}>
        <Icon name="calendar-check-outline" size={48} color={colors.gray} />
        <Text style={styles.emptyText}>{t('newHome.noSchedules')}</Text>
      </View>
    ),
    [t]
  );

  return (
    <View style={styles.root}>
      {/* Progress Bar */}
      <View style={styles.progressContainer}>
        <Text style={styles.progressLabel}>{t('newHome.progressThisYear')}</Text>
        <Text style={styles.progressValue}>{Math.round(displayPercentage)}%</Text>
      </View>
      <View style={styles.progressBarTrack}>
        <View style={[styles.progressBarFill, { width: `${Math.min(displayPercentage, 100)}%` }]} />
      </View>

      {/* Toggle: Today / Active Schedules */}
      <ScheduleToggle activeToggle={toggle} onToggle={handleToggleChange} primaryLabel={primaryToggleLabel} />

      {/* Date Display + Calendar (Today tab only — Active Schedules spans the whole period) */}
      {!isActiveMode && (
        <>
          <View style={styles.dateRow}>
            <Text style={styles.dateText}>{moment(selectedDate).format('dddd, D MMMM YYYY')}</Text>
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={handleCalendarToggle}
              style={[styles.calendarButton, isCalendarVisible ? styles.calendarButtonActive : null]}
            >
              <Icon name="calendar" size={18} color={colors.primary} />
              <Text style={styles.calendarButtonText}>{calendarToggleLabel}</Text>
              <Icon
                name={isCalendarVisible ? 'chevron-up' : 'chevron-down'}
                size={18}
                color={colors.primaryDark}
                style={styles.calendarButtonCaret}
              />
            </TouchableOpacity>
          </View>

          {isCalendarVisible && (
            <ScheduleCalendar
              currentMonth={calendarMonth}
              selectedDate={selectedDate}
              markedDates={calendarMarkedDates}
              minDate={calendarRange.minDate}
              maxDate={calendarRange.maxDate}
              onSelectDate={handleSelectCalendarDate}
              onChangeMonth={handleCalendarMonthChange}
            />
          )}
        </>
      )}

      {/* Search Bar */}
      <ScheduleSearchBar
        onChangeText={handleSearchChange}
        onFilterPress={() => setIsFilterModalVisible(true)}
        hasActiveFilter={hasActiveFilter}
      />

      {/* Schedule count + sync status */}
      <View style={styles.scheduleCountRow}>
        <View style={styles.scheduleCountLeft}>
          <Text style={styles.scheduleCountText}>
            {scheduleCountLabel} ({displayItemCount})
          </Text>
          {!displayIsFetching && (
            <View style={styles.syncBadge}>
              <Icon name="check-circle" size={14} color={colors.primary} />
              <Text style={styles.syncText}>{t('newHome.upToDate')}</Text>
            </View>
          )}
        </View>
        {timestamp && <Text style={styles.lastUpdatedText}>Last Updated {lastUpdatedText}</Text>}
      </View>

      {/* View By Chips (Today tab only — Active Schedules is grouped by date) */}
      {!isActiveMode && <ViewByChips activeView={viewBy} onViewChange={handleViewByChange} />}

      {!!displayFetchError && !displayIsFetching && (
        <Text style={styles.fetchErrorText}>Failed to refresh schedules. Please try again.</Text>
      )}

      {/* Schedule List */}
      {displayIsFetching ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={colors.primary} size="large" />
        </View>
      ) : isActiveMode ? (
        <SectionList
          sections={activeVisibleSections}
          renderItem={renderItem}
          renderSectionHeader={renderActiveSectionHeader}
          keyExtractor={keyExtractor}
          ListEmptyComponent={activeVisibleSections.length === 0 ? renderEmpty : null}
          contentContainerStyle={activeVisibleSections.length === 0 ? styles.emptyList : styles.listContent}
          showsVerticalScrollIndicator={false}
          stickySectionHeadersEnabled={false}
        />
      ) : viewBy === 'all' ? (
        <FlatList
          data={filteredItems}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          ListEmptyComponent={renderEmpty}
          contentContainerStyle={filteredItems.length === 0 ? styles.emptyList : styles.listContent}
          showsVerticalScrollIndicator={false}
        />
      ) : (
        <SectionList
          sections={visibleSections}
          renderItem={renderItem}
          renderSectionHeader={renderSectionHeader}
          keyExtractor={keyExtractor}
          ListEmptyComponent={visibleSections.length === 0 ? renderEmpty : null}
          contentContainerStyle={visibleSections.length === 0 ? styles.emptyList : styles.listContent}
          showsVerticalScrollIndicator={false}
          stickySectionHeadersEnabled={false}
        />
      )}

      {/* Filter & Sort modal */}
      <ScheduleFilterModal
        visible={isFilterModalVisible}
        initialFilter={filterState}
        availableSites={availableSites}
        onApply={handleFilterApply}
        onClose={() => setIsFilterModalVisible(false)}
      />

      {/* Check-in bottom sheet modal */}
      <HomeCheckInModal
        visible={showCheckInModal}
        item={selectedItem}
        isBusy={isCheckingIn}
        isOffline={isOffline}
        profileColor={profileColor}
        onClose={closeCheckInModal}
        onConfirmCheckIn={() => {
          if (selectedItem) {
            handleCheckIn(selectedItem, selectedItem.scheduleType === 'adhoc', closeCheckInModal);
          }
        }}
        onContinueDraft={() => {
          if (selectedItem) {
            handleContinueDraft(selectedItem, closeCheckInModal);
          }
        }}
        onResetDraft={() => {
          if (selectedItem) {
            handleResetDraft(selectedItem, closeCheckInModal);
          }
        }}
        onModifyPendingUpload={() => {
          if (selectedItem) {
            handleModifyPendingUpload(selectedItem, closeCheckInModal);
          }
        }}
        onReviewPendingUpload={() => {
          if (selectedItem) {
            handleSyncWithServer(selectedItem, closeCheckInModal);
          }
        }}
        onViewReport={handleViewReport}
      />

      {/* Confirmation modal (check-in confirm, GPS error, etc.) */}
      <ConfirmModal
        isVisible={showConfirmModal}
        language={language}
        title={modalConfig.title}
        subtitle={modalConfig.subtitle}
        options={modalConfig.options}
        onCloseModal={() => setShowConfirmModal(false)}
      />
    </View>
  );
};

export default NewHomeScreen;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#fff',
    position: 'relative'
  },
  // Progress bar
  progressContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: IS_SHORT_VIEWPORT ? 4 : 8,
    marginBottom: 4
  },
  progressLabel: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 14,
    color: colors.primary
  },
  progressValue: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 14,
    color: colors.primary
  },
  progressBarTrack: {
    height: 6,
    backgroundColor: '#e8f5e9',
    borderRadius: 3,
    marginHorizontal: 16,
    marginBottom: IS_SHORT_VIEWPORT ? 8 : 16
  },
  progressBarFill: {
    height: 6,
    backgroundColor: colors.primary,
    borderRadius: 3
  },
  // Date
  dateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: IS_SHORT_VIEWPORT ? 8 : 12
  },
  dateText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 18,
    color: '#25282B'
  },
  calendarButton: {
    minHeight: 40,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: 8
  },
  calendarButtonActive: {
    borderColor: colors.primary,
    backgroundColor: '#F1FCF5'
  },
  calendarButtonText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 13,
    color: colors.primaryDark,
    marginLeft: 8
  },
  calendarButtonCaret: {
    marginLeft: 4
  },
  // Schedule count
  scheduleCountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: IS_SHORT_VIEWPORT ? 8 : 15
  },
  scheduleCountLeft: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  scheduleCountText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 14,
    color: '#25282B'
  },
  syncBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 8
  },
  syncText: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 12,
    color: colors.primary,
    marginLeft: 4
  },
  lastUpdatedText: {
    ...fontMaker({ weight: 'Light' }),
    fontSize: 11,
    color: colors.gray,
    fontStyle: 'italic'
  },
  // Section headers
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginTop: 4,
    marginBottom: 10,
    backgroundColor: '#f5f5f5'
  },
  sectionHeaderTitleWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1
  },
  sectionHeaderText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 14,
    color: colors.darkgray
  },
  sectionHeaderCount: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 12,
    color: colors.gray,
    marginLeft: 8
  },
  sectionHeaderIcon: {
    marginLeft: 12
  },
  // Active Schedules date section headers
  dateSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 4,
    backgroundColor: '#fff'
  },
  dateSectionHeaderLeft: {
    flex: 1
  },
  dateSectionHeaderTitle: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 16,
    color: colors.primary
  },
  reportsCompletedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2
  },
  reportsCompletedLabel: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 13,
    color: '#25282B'
  },
  reportsCompletedValue: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 13,
    color: colors.secondary
  },
  // List
  listContent: {
    paddingBottom: 100
  },
  emptyList: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60
  },
  emptyText: {
    ...fontMaker({ weight: 'Light' }),
    fontSize: 16,
    color: colors.gray,
    marginTop: 12
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  fetchErrorText: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 12,
    color: colors.danger,
    marginHorizontal: 16,
    marginBottom: 8
  }
});
